import React, { useState, useRef } from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress, makeStyles } from '@material-ui/core'
import excelIcon from '../../assets/icons/excel-icon.svg'
import imageIcon from '../../assets/icons/image-icon.svg'
import pdfIcon from '../../assets/icons/pdf-icon.svg'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme => ({
  uploadArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '10px',
    textAlign: 'center',
    border: '1px dashed ' + theme.colors.textGray,
    fontSize: '10px',
    width: 'fit-content',
    borderRadius: '8px !important'
  },
  uploadBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
    cursor: 'pointer'
  },
  uploadButton: {
    color: theme.colors.blue,
    fontSize: '10px'
  },
  dropText: {
    fontSize: '10px'
  },
  attachmentButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: theme.colors.attachmentBackground,
    margin: '5px 0px',
    minHeight: '26px',
    minWidth: '150px',
    borderRadius: '8px',
    padding: '0px 10px'
  },
  fileTitle: {
    fontSize: 'inherit !important'
  },
  fileIcon: {
    height: '25px',
    backgroundColor: theme.colors.white,
    padding: '3px 5px',
    borderRadius: '4px'
  },
  deleteIcon: {
    fontSize: '12px',
    color: theme.colors.grey
  },

  loadingBox: {
    margin: 'auto'
  },
  loadingFiles: {
    marginLeft: 'auto',
    marginTop: '3px',
    color: theme.colors.iconBlue
  }
}))

const DragAndDropUploader = ({
  attachments,
  loadingFiles,
  onFilesSelect,
  onFileClick,
  onFileDelete,
  maxUploads = 4,
  uploadEnabled
}) => {
  const classes = useStyles()
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadLimitReached, setUploadLimitReached] = useState(false)
  const fileInputRef = useRef(null)
  const { t } = useTranslation()

  const handleFiles = files => {
    const newFiles = Array.from(files).slice(0, maxUploads - attachments.length)
    const updatedFiles = [...newFiles]

    setSelectedFiles(updatedFiles)
    onFilesSelect?.(updatedFiles)
  }

  const handleDrop = e => {
    e.preventDefault()
    if (!uploadEnabled) return
    const newFiles = Array.from(e.dataTransfer.files)
    const remainingSlots = maxUploads - attachments.length
    if (remainingSlots <= 0) {
      setUploadLimitReached(true)
      return
    }
    if (newFiles.length > remainingSlots) {
      setUploadLimitReached(true)
    }
    const filesToUpload = newFiles.slice(0, remainingSlots)
    handleFiles(filesToUpload)
  }

  const handleFileChange = e => {
    if (selectedFiles.length + attachments.length > maxUploads) return

    handleFiles(e.target.files)
  }

  const handleDragOver = e => {
    e.preventDefault()
  }

  const handleClick = () => {
    if (attachments.length < maxUploads) {
      fileInputRef.current.click()
    }
  }

  const handleFileClick = file => {
    onFileClick?.(file.content, file.title)
  }
  const handleRemove = fileToRemove => {
    setUploadLimitReached(false)
    onFileDelete?.(fileToRemove)
  }
  const getTruncatedText = text => {
    if (text.length > 20) {
      const start = text.slice(0, 8)
      const pointIndex = text.lastIndexOf('.')
      const end = text.slice(-(5 + (text.length - pointIndex)))
      return start + '...' + end
    }
    return text
  }

  const fileIcons = {
    pdf: pdfIcon,
    csv: excelIcon,
    xlsx: excelIcon,
    xls: excelIcon
  }

  const getIcon = fileType => fileIcons[fileType] || imageIcon

  const getFormattedFileSize = size => {
    if (!size) return
    if (size < 10000) return (size / 1024).toFixed(2) + ' KB'
    return (size / (1024 * 1024)).toFixed(2) + ' MB'
  }

  return (
    <Paper
      elevation={0}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className={classes.uploadArea}
    >
      {attachments.length < maxUploads && uploadEnabled && (
        <Box className={classes.uploadBox} onClick={handleClick}>
          <Typography>
            <span className={classes.uploadButton}>
              <FontAwesomeIcon icon="fa-solid fa-paperclip" />
              {' ' + t('general.labels.attach_files')}
            </span>
            <span className={classes.dropText}>
              {' ' + t('general.labels.or_drop_files')}
            </span>
          </Typography>
        </Box>
      )}
      {loadingFiles && (
        <Box className={classes.loadingBox}>
          <CircularProgress className={classes.loadingFiles} size={16} />
        </Box>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        hidden
        multiple
      />

      {selectedFiles.length + attachments.length > 0 && (
        <Box>
          {uploadLimitReached && (
            <span>{t('general.labels.max_uploads_reached')}</span>
          )}
          {attachments.map((file, index) => (
            <Box key={index} className={classes.attachmentButton}>
              <img src={getIcon(file.type)} className={classes.fileIcon} />
              <span onClick={() => handleFileClick(file)}>
                <Typography className={classes.fileTitle}>
                  {getTruncatedText(file ? file.title : '')}
                </Typography>
                {getFormattedFileSize(file?.size)}
              </span>
              {uploadEnabled && (
                <FontAwesomeIcon
                  className={classes.deleteIcon}
                  icon="fa-regular fa-circle-xmark"
                  onClick={() => handleRemove(file)}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}

export default DragAndDropUploader
